import { prepareWixBrand } from '../utils/wixPreparation';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { wixRequest } from '../utils/wixProductApi';
import { fetchAgent } from './agent-creation/steps';
import useTrainingTracker from './agent-creation/useTrainingTracker';
import { resolveWixFlow, WIX_STEP_COMPONENTS } from './agent-creation/wixFlow';
import { authFetch } from '../utils/api';
import { getCurrentOrgId, setCurrentOrgId, setRole, clearWorkspaceScopedSelections } from '../utils/permissions';
import WixAppOnboarding, { WIX_APP_STEPS } from './wix-apps/WixAppOnboarding';
import './agent-creation/AgentCreation.css';

// The acquisition app orders onboarding only. Each step edits the normal agent
// through the existing portal APIs; completion opens the normal portal.
// Every flow whose steps the Wix apps' onboarding draws gets the main Wix onboarding's look (WixAppOnboarding); an
// installation still on an older flow with another step (Real Estate's "channels") finishes in the previous renderer.
export default function WixProductOnboarding({ appKey, state, reload, openAgent }) {
  const flow = resolveWixFlow(state);
  if (!flow) return <div role="alert">This onboarding experience is not available yet.</div>;
  if (flow.steps.every(step => WIX_APP_STEPS.has(step))) {
    return <WixAppOnboarding appKey={appKey} state={state} flow={flow} reload={reload} openAgent={openAgent} />;
  }
  return <GuidedOnboarding appKey={appKey} state={state} flow={flow} reload={reload} openAgent={openAgent} />;
}

function GuidedOnboarding({ appKey, state, flow, reload, openAgent }) {
  const { t } = useTranslation();
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const beforeLeave = useRef(null);
  const contextGenerated = useRef(null);
  const registerBeforeLeave = useCallback(fn => { beforeLeave.current = fn; }, []);
  const steps = flow.steps;
  const index = Math.max(0, Math.min(state.step || 0, steps.length - 1));
  const key = steps[index];
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 4200);
    return () => clearTimeout(timer);
  }, [notice]);
  const refreshAgent = useCallback(async () => {
    const value = await fetchAgent(state.bot_id);
    if (!value) throw new Error(t('wix_multi_agent_load_error'));
    setAgent(value);
    return value;
  }, [state.bot_id, t]);
  const onTrainingDone = useCallback(async () => {
    await refreshAgent();
    if (contextGenerated.current === state.bot_id) return;
    contextGenerated.current = state.bot_id;
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}onboarding/generate_business_context`, {
        method:'POST', body:JSON.stringify({bot_id:state.bot_id}),
      });
      if (!response.ok) contextGenerated.current = null;
    } catch { contextGenerated.current = null; }
  }, [state.bot_id, refreshAgent]);
  const tracker = useTrainingTracker({botId:state.bot_id,
    userId:localStorage.getItem('user_id'), onAllDone:onTrainingDone});
  useEffect(() => {
    let active = true;
    (async () => {
      if (state.organization_id && String(getCurrentOrgId()) !== String(state.organization_id)) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}orgs/switch`, {
          method: 'POST', headers: {'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('token')}`},
          body: JSON.stringify({organization_id:state.organization_id}),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || t('wix_multi_agent_load_error'));
        localStorage.setItem('token', data.token);
        setRole(data.role, data.owner_user_id); setCurrentOrgId(data.current_org_id);
        clearWorkspaceScopedSelections();
      }
      if (active) {
        await prepareWixBrand(state.bot_id).catch(() => {});
        if (active) await refreshAgent();
      }
    })().catch(e => active && setError(e.message));
    return () => { active = false; };
  }, [state.organization_id, state.bot_id, refreshAgent, t]);

  const move = async (next, complete = false, exit = false) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      if (beforeLeave.current) await beforeLeave.current();
      await wixRequest(appKey, 'progress', {method:'PUT', body:JSON.stringify({
        step:next, revision:state.revision, draft:state.draft || {}, complete,
      })});
      await reload();
      if (complete || exit) {
        const result = await openAgent();
        if (result?.error) throw new Error(result.error);
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const shared = { agent, botId:state.bot_id, refreshAgent,
    chatAppearance:flow.chat_appearance === 'default' ? undefined : flow.chat_appearance,
    onFinish:() => move(index, true),
    goNext:() => index === steps.length - 1 ? move(index, true) : move(index + 1),
    goBack:index > 0 ? () => move(index - 1) : undefined, allowBack:index > 0,
    goTo:step => move(Math.max(0, steps.indexOf(step))), exit:() => move(index, false, true),
    notify:setNotice, isWebflow:false, returnPath:`/wix/${appKey}/start`, busy,
    // The installed Wix site and the owner's address, so Knowledge and Notifications start pre-filled.
    prefillUrl:state.prefill?.site_url || '', prefillEmail:state.prefill?.email || '',
    // The app's own Wix listing prices for the plan step (flow plan_prices); absent = internal plan prices.
    planPrices:flow.plan_prices || null,
    primaryLabel:index === steps.length - 1 ? t('wix_multi_finish') : undefined,
    track:tracker.track, strips:<div className="ac-strips" role="status">{Object.entries(tracker.jobs).map(([kind, job]) =>
      <div key={kind} className={`ac-strip ${job.phase === 'done' ? 'is-done' : ''}`}>
        <span className="ac-strip-label">{t(job.phase === 'done' ? `ac_strip_done_${kind}` : `ac_strip_progress_${kind}`, { count:job.total, done:job.done, total:job.total })}</span>
        {job.phase !== 'done' && <div className="ac-strip-bar"><span className="ac-strip-fill" style={{ width:`${Math.round(job.done / Math.max(1,job.total) * 100)}%` }} /></div>}
      </div>)}</div>,
    training:Object.values(tracker.jobs).some(j => j.phase !== 'done'), registerBeforeLeave };
  const Component = WIX_STEP_COMPONENTS[key];
  return <div className="ac-page wix-product-onboarding">
    <div className="ac-bg-grid" aria-hidden="true" /><div className="ac-bg-glow" aria-hidden="true" />
    <header className="ac-header">
      <img className="ac-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
      <nav className="ac-steps" aria-label={t('ac_steps_aria')}>
        {steps.map((step, i) => <div key={step} className="ac-step-item">
          <button className={`ac-step ac-step-${i === index ? 'current' : i < index ? 'done' : 'todo'}`}
            aria-current={i === index ? 'step' : undefined} disabled={busy || i >= index} onClick={() => move(i)}>
            <svg className="ac-step-mark" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true"><circle className="ac-step-mark-circle" cx="14" cy="14" r="12" />
              {i < index ? <path className="ac-step-mark-check" d="m8.5 14 3.5 3.5 7.5-7.5" fill="none" strokeWidth="2.4" /> : <text className="ac-step-mark-num" x="14" y="14" dy="0.35em" textAnchor="middle">{i + 1}</text>}
            </svg><span className="ac-step-label">{t(`ac_step_${step}`)}</span>
          </button>
          {i < steps.length - 1 && <span className={`ac-connector ${i < index ? 'is-done' : ''}`} aria-hidden="true"><span className="ac-connector-fill" /></span>}
        </div>)}
      </nav>
      <button className="ac-exit" onClick={shared.exit} disabled={busy}>{t('wix_multi_save_exit')}</button>
    </header>
    <main className="ac-main"><div className={`ac-card ac-card-${key}`} style={{maxWidth:key === 'design' || key === 'plan' ? 1160 : key === 'instagram' || key === 'whatsapp' ? 820 : 940}} aria-busy={busy}>
      {error && <div className="ac-error wix-product-flow-error" role="alert">{error}</div>}
      {!agent ? <div className="ac-loading" role="status"><span className="ac-spinner" /><span className="sr-only">{t('loading')}</span></div> : <Component key={key} {...shared} />}
    </div></main>
    {notice && <div className="ac-toast ac-toast-ok" role="status"><span>{notice}</span></div>}
  </div>;
}
